import {
  Card,
  CardBody,
  Col,
  Row,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText,
  Input,
} from "reactstrap";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import CommonCardHeader from "../../../CommonComponents/CommonCardHeader/CommonCardHeader";
import DataTable from "react-data-table-component";
import FilterComponent from "../../../DataTables/Common/FilterComponent";

// Table
import { EmpDeploymentsTableColumn } from "../../../../Data/DataTables/EmployeeDeployments";
import SubDeploymentsTable from "../../../DataTables/Employee/SubDeploymentsTable";

// Modals
import CreateDeploymentModal from "../../../UiKits/Modal/Employee/CreateDeployment";

// Services
import {
  VIEW_EMPDEPLOYMENTS_QUERY_KEY,
  VIEW_EMPDEPLOYMENTS_MAIN_QUERY_KEY,
  employeeDeploymentService,
} from "../../../../Services/employeeDeployments";

// Type
import { ViewEmployeeDeployments } from "../../../../Types/Employee/EmployeeDeployments";
import { CRUDPermissions } from "../../../../Types/Common/Access";

interface EmployeeProfileProps {
  EmpNo: string;
  isAdmin: boolean;
  access: CRUDPermissions;
}

const EmployeeDeployment: React.FC<EmployeeProfileProps> = ({
  EmpNo,
  isAdmin,
  access,
}) => {
  const [formData, setFormData] = useState<ViewEmployeeDeployments | null>(
    null,
  );
  const [filterText, setFilterText] = useState("");

  const customStyles = {
    rows: {
      style: {
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        whiteSpace: "nowrap",
        marginTop: "8px",
        marginBottom: "8px",
      },
    },
  };

  const { data: dataResponse, isLoading: dataisFetching } = useQuery({
    queryKey: [VIEW_EMPDEPLOYMENTS_QUERY_KEY, EmpNo],
    queryFn: () => employeeDeploymentService.ViewByEmployeeNo(EmpNo),
    enabled: !!EmpNo,
  });

  const filteredItems: ViewEmployeeDeployments[] =
    [...(dataResponse ?? [])]
      .filter(
        (item: ViewEmployeeDeployments) =>
          Number(item.is_main) === 1 &&
          Object.values(item).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(filterText.toLowerCase()),
          ),
      )
      .sort(
        (a, b) =>
          new Date(b.effectivity_date).getTime() -
            new Date(a.effectivity_date).getTime() || b.id - a.id,
      ) ?? [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const activeDeploymentId = [...(dataResponse ?? [])]
    .filter((item: ViewEmployeeDeployments) => {
      const effectivityDate = new Date(`${item.effectivity_date}T00:00:00`);

      return Number(item.is_main) === 1 && effectivityDate <= today;
    })
    .sort(
      (a: ViewEmployeeDeployments, b: ViewEmployeeDeployments) =>
        b.effectivity_date.localeCompare(a.effectivity_date) || b.id - a.id,
    )[0]?.id;

  const { data: MainDepData, isLoading: MainDepisFetching } = useQuery({
    queryKey: [VIEW_EMPDEPLOYMENTS_MAIN_QUERY_KEY, EmpNo],
    queryFn: () => employeeDeploymentService.ViewEmployeeMain(EmpNo),
    enabled: !!EmpNo,
  });

  useEffect(() => {
    if (MainDepData) {
      setFormData(MainDepData);
    }
  }, [MainDepData]);

  const columns = EmpDeploymentsTableColumn(
    dataResponse?.length ?? 0,
    activeDeploymentId,
    access,
  );

  return (
    <Row>
      <Col md="12">
        <Card className="card-absolute">
          <CommonCardHeader
            headClass="bg-primary"
            tagClass="txt-light"
            icon={<i className="icofont icofont-location-pin me-2"></i>}
            title={"Main Deployment"}
          />
          <CardBody>
            <Row className="mb-3">
              <Col md="12">
                <FormGroup>
                  <Label>Company</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-badge txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.company_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Role</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-briefcase-alt-1 txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.role_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Division</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-building-alt txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.division_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Department</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-building-alt txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.department_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Region</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.region_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Cluster</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.cluster_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>Deployment Site</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="bg-white text-color-light"
                      disabled
                      value={
                        MainDepisFetching
                          ? "Loading data..."
                          : formData?.deployment_site_name?.toUpperCase() ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="12" className="mt-2">
                {access.create && access.update && (
                  <CreateDeploymentModal EmpNo={EmpNo} />
                )}

                <br></br>
                <FilterComponent
                  onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilterText(e.target.value)
                  }
                  filterText={filterText}
                />
                <div className="table-responsive">
                  <DataTable
                    className="display nowrap custom-scrollbar"
                    data={filteredItems}
                    columns={columns}
                    striped={true}
                    pagination
                    progressPending={dataisFetching}
                    highlightOnHover={true}
                    fixedHeader={true}
                    customStyles={customStyles}
                  />
                </div>
              </Col>

              {/* {isAdmin && <UpdateEmpInfoModal EmpNo={EmpNo} />} */}
            </Row>
          </CardBody>
        </Card>
      </Col>

      <Col md="12">
        <Card className="card-absolute">
          <CommonCardHeader
            headClass="bg-primary"
            tagClass="txt-light"
            icon={<i className="icofont icofont-building-alt me-2"></i>}
            title={"Deployment Sites"}
          />
          <CardBody>
            <Row className="mb-3">
              <Col md="12" className="mt-2">
                <SubDeploymentsTable
                  EmpNo={EmpNo}
                  isAdmin={isAdmin}
                  access={access}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
export default EmployeeDeployment;
